import React, { useState, useContext, useRef, forwardRef, useImperativeHandle } from "react";

import { ja, ar, es, hi, zhCN, enUS } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { API, graphqlOperation, Storage } from "aws-amplify";
import { createAttachment, createArticle, createArticleLabelJoin } from "../../graphql/mutations";

import { useAuthDataContext } from "../../containers/AuthDataProvider";
import InputImageWithPreview from "../../components/part/InputImageWithPreview";

import makeStyles from "@mui/styles/makeStyles";
import TextField from "@mui/material/TextField";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { UserSetting } from "../../App";

import { animateScroll as scroll } from "react-scroll";

import LabelEdit from "../../components/part/LabelEdit";
import InputLocation from "../../components/part/InputLocation";
import "../part/CKEditor_custom.scss";

import { useIntl } from "react-intl";
const defaults = require("../../translations/en.json");

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "66px",
    [theme.breakpoints.up("sm")]: {
      marginTop: "74px",
    },
    "& > *": {
      margin: theme.spacing(1),
      width: "95vw",
    },
  },
}));

const initialState = {
  name: "",
  description: "",
  postedDate: new Date().toISOString(),
};

const ArticleForm = forwardRef((props, ref) => {
  const { onClose, handleSplashIcon, setOpenBackdrop, handleOpenSnackbar } = props;
  const user = useAuthDataContext();
  const { formatMessage } = useIntl();

  const [formState, setFormState] = useState(initialState);

  const fileRef = useRef();

  function setInput(key, value) {
    setFormState({ ...formState, [key]: value });
  }

  // const handlePostedDateChange = (newValue: Dayjs | null) => {
  const handlePostedDateChange = (newValue) => {
    setInput("postedDate", newValue);
  };

  function createArticleLabelJoinAPI(articleId, postDate, labelId) {
    try {
      const articleLabelJoinVariables = {
        input: {
          articleId: articleId,
          labelId: labelId,
          postedDate: postDate,
          owner: user.username,
        },
      };

      API.graphql(graphqlOperation(createArticleLabelJoin, articleLabelJoinVariables))
        .then((result) => {})
        .catch((err) => {});
    } catch (err) {
    } finally {
    }
  }

  async function createArticleLabelJoinService(articleId, postDate) {
    for (let k = 0; k < labels.length; k++) {
      // console.log('labels['+ k + ']',labels[k]);
      // console.log(
      //   'articleLabelItems.some(i => i.labelId === labels[k].id)',
      //   articleLabelItems.some(i => i.labelId === labels[k].id)
      // );

      if (articleLabelItems.some((i) => i.labelId === labels[k].id)) {
        // console.log('only after labels['+ k + ']',labels[k]);
        createArticleLabelJoinAPI(articleId, postDate, labels[k].id);
      }
    }
  }

  async function createArticleAPI() {
    // Amplifyが自動的にUUID idを生成するため、手動でidを設定しない
    const variables = {
      input: {
        ...formState,
        owner: user.username,
        latitude: latitude,
        longitude: longitude,
        positionName: positionName,
      },
    };
    setFormState(initialState);

    return API.graphql(graphqlOperation(createArticle, variables))
      .then((result) => {
        handleSplashIcon();
        setOpenBackdrop(false);
        // 生成されたIDを返す
        return result.data.createArticle.id;
      })
      .catch((err) => {
        setOpenBackdrop(false);

        if (err.errors === undefined) {
          handleOpenSnackbar("" + err.code + ":" + err.message);
        } else {
          handleOpenSnackbar(err.errors[0].message);
        }
        throw err;
      });
  }

  //チェック対象のする拡張子
  var allow_exts = ["jpg", "jpeg", "png"];

  //アップロード予定のファイル名の拡張子を確認する関数
  function checkExt(filename) {
    //比較のため小文字にする
    var ext = getExt(filename).toLowerCase();
    //許可する拡張子の一覧(allow_exts)から対象の拡張子があるか確認する
    if (allow_exts.indexOf(ext) === -1) return false;
    return true;
  }

  //ファイル名から拡張子を取得する関数
  function getExt(filename) {
    var pos = filename.lastIndexOf(".");
    if (pos === -1) return "";
    return filename.slice(pos + 1);
  }

  useImperativeHandle(ref, () => ({
    createArticleService: async () => {
      setOpenBackdrop(true);
      try {
        let postDate = formState.postedDate;

        const length = fileRef.current.files.length;
        if (length > 0) {
          // すべてのファイルアップロードを配列に格納
          const uploadPromises = [];

          for (var i = 0; i < fileRef.current.files.length; i++) {
            var fileName = fileRef.current.files[i].name;
            var fileType = fileRef.current.files[i].type;

            // 拡張子を確認し、直前にサムネイルではないことを示すprefixを追加する
            // s3登録時をトリガに、サムネイルではないことを示すprefixがあることをチェックして、
            // lambda で s3にサムネイルを作成する
            if (checkExt(fileName)) {
              var pos = fileName.lastIndexOf(".");
              if (pos !== -1) {
                var fileExt = fileName.slice(pos + 1);
                var fileBody = fileName.slice(0, pos);
                fileName = fileBody + "-o." + fileExt;
              }
            }

            // Storage.putをPromise配列に追加
            uploadPromises.push(
              Storage.put(postDate + fileName, fileRef.current.files[i], {
                level: "private",
                contentType: fileType,
              })
            );
          }

          // すべてのアップロードが完了するまで待つ
          await Promise.all(uploadPromises);

          // 記事を作成してIDを取得
          const createdArticleId = await createArticleAPI();

          // 生成されたIDを使用してAttachmentを作成
          const attachmentPromises = [];
          for (var j = 0; j < fileRef.current.files.length; j++) {
            var attachmentFileName = fileRef.current.files[j].name;
            var attachmentFileType = fileRef.current.files[j].type;

            if (checkExt(attachmentFileName)) {
              var attachmentPos = attachmentFileName.lastIndexOf(".");
              if (attachmentPos !== -1) {
                var attachmentFileExt = attachmentFileName.slice(attachmentPos + 1);
                var attachmentFileBody = attachmentFileName.slice(0, attachmentPos);
                attachmentFileName = attachmentFileBody + "-o." + attachmentFileExt;
              }
            }

            const attachmentVariables = {
              input: {
                contentType: attachmentFileType,
                path: postDate + attachmentFileName,
                postedDate: postDate,
                owner: user.username,
                articleId: createdArticleId,
              },
            };

            attachmentPromises.push(
              API.graphql(graphqlOperation(createAttachment, attachmentVariables))
            );
          }

          // ラベルとAttachmentを作成
          await createArticleLabelJoinService(createdArticleId, postDate);
          await Promise.all(attachmentPromises).catch((err) => {
            console.log("Attachment creation error:", err);
          });
        } else {
          const createdArticleId = await createArticleAPI();
          await createArticleLabelJoinService(createdArticleId, postDate);
        }
        onClose();
        scroll.scrollToTop();
      } catch (err) {
        console.log("Article creation error:", err);
      } finally {
      }
    },
  }));

  const classes = useStyles();

  const { switchState } = useContext(UserSetting);

  const [labels, setLabels] = useState([]);
  const [articleLabelItems, setArticleLabelItems] = useState([]);

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [positionName, setPositionName] = useState(null);

  function setPositionSet(_latitude, _longitude, _positionName) {
    setLatitude(_latitude);
    setLongitude(_longitude);
    setPositionName(_positionName);
  }

  const editorConfiguration = {
    // plugins: [Markdown],
    toolbar: {
      // viewportTopOffset: 56,
      items: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "bulletedList",
        "numberedList",
        // "todoList",
        // "|",
        // "fontfamily",
        // "fontsize",
        // "|",
        // "alignment",
        // // "|",
        // "fontColor",
        // "fontBackgroundColor",
        // "|",
        "bold",
        "italic",
        // "strikethrough",
        // "underline",
        // "subscript",
        // "superscript",
        // "uploadImage",
        "|",
        "blockQuote",
        // "code",
        // "codeBlock",
        "|",
        "link",
        // "|",
        // "outdent",
        // "indent",
        // "|",
        "|",
        "insertTable",
      ],
    },
  };

  const editorConfigurationRtl = {
    contentsLangDirection: "rtl",
    language: {
      // The UI will be in Arbic.
      ui: "ar",

      // The content will be edited in Arabic.
      content: "ar",
    },
    // plugins: [Markdown],
    toolbar: {
      // viewportTopOffset: 56,
      items: [
        "undo",
        "redo",
        "|",
        "heading",
        "|",
        "bulletedList",
        "numberedList",
        // "todoList",
        // "|",
        // "fontfamily",
        // "fontsize",
        // "|",
        // "alignment",
        // // "|",
        // "fontColor",
        // "fontBackgroundColor",
        // "|",
        "bold",
        "italic",
        // "strikethrough",
        // "underline",
        // "subscript",
        // "superscript",
        // "uploadImage",
        "|",
        "blockQuote",
        // "code",
        // "codeBlock",
        "|",
        "link",
        // "|",
        // "outdent",
        // "indent",
        // "|",
        "|",
        "insertTable",
      ],
    },
  };

  //langの選択状況に応じてdate-fnsのロケールを切り替える
  const switchLocale = () => {
    switch (sessionStorage.getItem("lang")) {
      case "ar":
        return ar;
      case "hi":
        return hi;
      case "es":
        return es;
      case "ja":
        return ja;
      case "zh":
        return zhCN;
      default:
        return enUS;
    }
  };

  return (
    <div id="articleform_root" className={classes.root} noValidate autoComplete="off">
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={switchLocale()}>
        <DateTimePicker
          label={formatMessage({
            id: "date",
            defaultMessage: defaults["date"],
          })}
          id="postedDate"
          value={formState.postedDate}
          onChange={handlePostedDateChange}
          renderInput={(params) => <TextField {...params} />}
        />
      </LocalizationProvider>

      <TextField
        margin="dense"
        id="name"
        label={formatMessage({
          id: "title",
          defaultMessage: defaults["title"],
        })}
        type="text"
        onChange={(event) => setInput("name", event.target.value)}
        value={formState.name}
        fullWidth
      />

      {switchState ? (
        <TextField
          margin="dense"
          id="description"
          label={formatMessage({
            id: "description",
            defaultMessage: defaults["description"],
          })}
          type="text"
          onChange={(event) => setInput("description", event.target.value)}
          value={formState.description}
          fullWidth
          multiline
        />
      ) : (
        <CKEditor
          editor={ClassicEditor}
          config={
            sessionStorage.getItem("lang") !== "ar" ? editorConfiguration : editorConfigurationRtl
          }
          data={formState.description}
          onReady={(editor) => {
            // You can store the "editor" and use when it is needed.
            // console.log("Editor is ready to use!", editor);
          }}
          onChange={(event, editor) => {
            const data = editor.getData();
            // console.log({ event, editor, data });
            setInput("description", data);
          }}
          onBlur={(event, editor) => {}}
          onFocus={(event, editor) => {}}
        />
      )}

      {/* <FormControlLabel
        className="switch_html"
        control={
          <Switch checked={switchState} onChange={changeSwitch} name="html" />
        }
        label="HTML"
      /> */}

      <LabelEdit
        labels={labels}
        setLabels={setLabels}
        articleLabelItems={articleLabelItems}
        setArticleLabelItems={setArticleLabelItems}
        user={user}
      />

      <InputImageWithPreview fileRef={fileRef} />

      <InputLocation
        latitude={latitude}
        longitude={longitude}
        positionName={positionName}
        setPositionSet={setPositionSet}
        setPositionName={setPositionName}
      />
    </div>
  );
});

export default ArticleForm;
